// Static reference data for the WoW combat log analyzer.
//
// Everything here is version-tolerant on purpose: the parser never fails on an
// unknown id, it falls back to showing the raw number so the UI can say
// "no data" instead of inventing a label.

export const SUPPORTED_LOG_VERSIONS = [20, 21, 22]

// COMBATLOG_OBJECT_* bit flags, used to tell players, pets and NPCs apart
// without looking at localized names.
export const UNIT_FLAGS = {
    AFFILIATION_MINE: 0x1,
    AFFILIATION_PARTY: 0x2,
    AFFILIATION_RAID: 0x4,
    AFFILIATION_OUTSIDER: 0x8,
    REACTION_FRIENDLY: 0x10,
    REACTION_NEUTRAL: 0x20,
    REACTION_HOSTILE: 0x40,
    CONTROL_PLAYER: 0x100,
    CONTROL_NPC: 0x200,
    TYPE_PLAYER: 0x400,
    TYPE_NPC: 0x800,
    TYPE_PET: 0x1000,
    TYPE_GUARDIAN: 0x2000,
    TYPE_OBJECT: 0x4000,
}

export const SPELL_SCHOOLS = {
    1: 'Physical',
    2: 'Holy',
    4: 'Fire',
    8: 'Nature',
    16: 'Frost',
    32: 'Shadow',
    64: 'Arcane',
}

export const POWER_TYPES = {
    '-2': 'Health',
    0: 'Mana',
    1: 'Rage',
    2: 'Focus',
    3: 'Energy',
    4: 'Combo points',
    5: 'Runes',
    6: 'Runic power',
    7: 'Soul shards',
    8: 'Lunar power',
    9: 'Holy power',
    11: 'Maelstrom',
    12: 'Chi',
    13: 'Insanity',
    17: 'Fury',
    18: 'Pain',
    19: 'Essence',
}

// Instance difficulty ids seen in ENCOUNTER_START / ZONE_CHANGE.
export const DIFFICULTIES = {
    1: 'Normal dungeon',
    2: 'Heroic dungeon',
    8: 'Mythic Keystone',
    14: 'Normal raid',
    15: 'Heroic raid',
    16: 'Mythic raid',
    17: 'LFR',
    23: 'Mythic dungeon',
    24: 'Timewalking',
    33: 'Timewalking raid',
    151: 'Timewalking (LFR)',
    167: 'Torghast',
    205: 'Follower dungeon',
    208: 'Delve',
}

// specId -> { class, spec, role }. Unknown ids are rendered as "Spec #id" with
// no role, which is the honest answer for a brand new spec.
const SPEC_LIST = [
    [250, 'Death Knight', 'Blood', 'tank'],
    [251, 'Death Knight', 'Frost', 'dps'],
    [252, 'Death Knight', 'Unholy', 'dps'],
    [577, 'Demon Hunter', 'Havoc', 'dps'],
    [581, 'Demon Hunter', 'Vengeance', 'tank'],
    [102, 'Druid', 'Balance', 'dps'],
    [103, 'Druid', 'Feral', 'dps'],
    [104, 'Druid', 'Guardian', 'tank'],
    [105, 'Druid', 'Restoration', 'healer'],
    [1467, 'Evoker', 'Devastation', 'dps'],
    [1468, 'Evoker', 'Preservation', 'healer'],
    [1473, 'Evoker', 'Augmentation', 'dps'],
    [253, 'Hunter', 'Beast Mastery', 'dps'],
    [254, 'Hunter', 'Marksmanship', 'dps'],
    [255, 'Hunter', 'Survival', 'dps'],
    [62, 'Mage', 'Arcane', 'dps'],
    [63, 'Mage', 'Fire', 'dps'],
    [64, 'Mage', 'Frost', 'dps'],
    [268, 'Monk', 'Brewmaster', 'tank'],
    [270, 'Monk', 'Mistweaver', 'healer'],
    [269, 'Monk', 'Windwalker', 'dps'],
    [65, 'Paladin', 'Holy', 'healer'],
    [66, 'Paladin', 'Protection', 'tank'],
    [70, 'Paladin', 'Retribution', 'dps'],
    [256, 'Priest', 'Discipline', 'healer'],
    [257, 'Priest', 'Holy', 'healer'],
    [258, 'Priest', 'Shadow', 'dps'],
    [259, 'Rogue', 'Assassination', 'dps'],
    [260, 'Rogue', 'Outlaw', 'dps'],
    [261, 'Rogue', 'Subtlety', 'dps'],
    [262, 'Shaman', 'Elemental', 'dps'],
    [263, 'Shaman', 'Enhancement', 'dps'],
    [264, 'Shaman', 'Restoration', 'healer'],
    [265, 'Warlock', 'Affliction', 'dps'],
    [266, 'Warlock', 'Demonology', 'dps'],
    [267, 'Warlock', 'Destruction', 'dps'],
    [71, 'Warrior', 'Arms', 'dps'],
    [72, 'Warrior', 'Fury', 'dps'],
    [73, 'Warrior', 'Protection', 'tank'],
]

export const SPECIALIZATIONS = SPEC_LIST.reduce((map, [id, className, spec, role]) => {
    map[id] = { className, spec, role }
    return map
}, {})

export const CLASS_COLORS = {
    'Death Knight': '#c41e3a',
    'Demon Hunter': '#a330c9',
    Druid: '#ff7c0a',
    Evoker: '#33937f',
    Hunter: '#aad372',
    Mage: '#3fc7eb',
    Monk: '#00ff98',
    Paladin: '#f48cba',
    Priest: '#ffffff',
    Rogue: '#fff468',
    Shaman: '#0070dd',
    Warlock: '#8788ee',
    Warrior: '#c69b6d',
}

// Only affixes with a long-standing, stable name are listed. Everything else is
// resolved at runtime from the Raider.IO affix endpoint, or shown as its raw id.
export const KNOWN_AFFIXES = {
    1: 'Overflowing',
    2: 'Skittish',
    3: 'Volcanic',
    4: 'Necrotic',
    5: 'Teeming',
    6: 'Raging',
    7: 'Bolstering',
    8: 'Sanguine',
    9: 'Tyrannical',
    10: 'Fortified',
    11: 'Bursting',
    12: 'Grievous',
    13: 'Explosive',
    14: 'Quaking',
    117: 'Reaping',
    119: 'Beguiling',
    120: 'Awakened',
    121: 'Prideful',
    122: 'Inspiring',
    123: 'Spiteful',
    124: 'Storming',
    128: 'Tormented',
    129: 'Infernal',
    130: 'Encrypted',
    131: 'Shrouded',
    132: 'Thundering',
    134: 'Entangling',
    135: 'Afflicted',
    136: 'Incorporeal',
}

// Defensive cooldowns, matched by spell id only.
//
// A name match would be wrong in a localized log and a guess would be worse
// than nothing, so an id that is not listed here is simply not labelled as a
// defensive: the aura still shows up, it just carries no claim about what it
// does. `kind` separates what a player presses on themselves from what someone
// else casts on them, because that is the distinction worth seeing next to a
// death.
const DEFENSIVE_LIST = [
    // Death Knight
    [48792, 'Icebound Fortitude', 'personal'],
    [48707, 'Anti-Magic Shell', 'personal'],
    [51052, 'Anti-Magic Zone', 'raid'],
    [55233, 'Vampiric Blood', 'personal'],
    [49028, 'Dancing Rune Weapon', 'personal'],
    [49039, 'Lichborne', 'personal'],
    [194679, 'Rune Tap', 'personal'],
    // Demon Hunter
    [198589, 'Blur', 'personal'],
    [196555, 'Netherwalk', 'immunity'],
    [196718, 'Darkness', 'raid'],
    [187827, 'Metamorphosis', 'personal'],
    [203720, 'Demon Spikes', 'personal'],
    [204021, 'Fiery Brand', 'personal'],
    // Druid
    [22812, 'Barkskin', 'personal'],
    [61336, 'Survival Instincts', 'personal'],
    [22842, 'Frenzied Regeneration', 'personal'],
    [192081, 'Ironfur', 'personal'],
    [155835, 'Bristling Fur', 'personal'],
    [108238, 'Renewal', 'personal'],
    [740, 'Tranquility', 'raid'],
    // Evoker
    [363916, 'Obsidian Scales', 'personal'],
    [374348, 'Renewing Blaze', 'personal'],
    [374227, 'Zephyr', 'raid'],
    [363534, 'Rewind', 'raid'],
    // Hunter
    [186265, 'Aspect of the Turtle', 'immunity'],
    [264735, 'Survival of the Fittest', 'personal'],
    [109304, 'Exhilaration', 'personal'],
    // Mage
    [45438, 'Ice Block', 'immunity'],
    [11426, 'Ice Barrier', 'personal'],
    [235313, 'Blazing Barrier', 'personal'],
    [235450, 'Prismatic Barrier', 'personal'],
    [108978, 'Alter Time', 'personal'],
    [110959, 'Greater Invisibility', 'personal'],
    [414660, 'Mass Barrier', 'raid'],
    // Monk
    [115203, 'Fortifying Brew', 'personal'],
    [243435, 'Fortifying Brew', 'personal'],
    [122470, 'Touch of Karma', 'personal'],
    [122783, 'Diffuse Magic', 'personal'],
    [122278, 'Dampen Harm', 'personal'],
    [115176, 'Zen Meditation', 'personal'],
    [322507, 'Celestial Brew', 'personal'],
    [116849, 'Life Cocoon', 'external'],
    [115310, 'Revival', 'raid'],
    // Paladin
    [642, 'Divine Shield', 'immunity'],
    [498, 'Divine Protection', 'personal'],
    [31850, 'Ardent Defender', 'personal'],
    [86659, 'Guardian of Ancient Kings', 'personal'],
    [1022, 'Blessing of Protection', 'external'],
    [6940, 'Blessing of Sacrifice', 'external'],
    [633, 'Lay on Hands', 'external'],
    [184662, 'Shield of Vengeance', 'personal'],
    [31821, 'Aura Mastery', 'raid'],
    // Priest
    [47585, 'Dispersion', 'personal'],
    [33206, 'Pain Suppression', 'external'],
    [47788, 'Guardian Spirit', 'external'],
    [62618, 'Power Word: Barrier', 'raid'],
    [19236, 'Desperate Prayer', 'personal'],
    [15286, 'Vampiric Embrace', 'raid'],
    // Rogue
    [31224, 'Cloak of Shadows', 'personal'],
    [5277, 'Evasion', 'personal'],
    [1966, 'Feint', 'personal'],
    [185311, 'Crimson Vial', 'personal'],
    // Shaman
    [108271, 'Astral Shift', 'personal'],
    [108270, 'Stone Bulwark Totem', 'personal'],
    [98008, 'Spirit Link Totem', 'raid'],
    [108280, 'Healing Tide Totem', 'raid'],
    // Warlock
    [104773, 'Unending Resolve', 'personal'],
    [108416, 'Dark Pact', 'personal'],
    [6262, 'Healthstone', 'personal'],
    // Warrior
    [871, 'Shield Wall', 'personal'],
    [12975, 'Last Stand', 'personal'],
    [118038, 'Die by the Sword', 'personal'],
    [23920, 'Spell Reflection', 'personal'],
    [97462, 'Rallying Cry', 'raid'],
    [184364, 'Enraged Regeneration', 'personal'],
    [190456, 'Ignore Pain', 'personal'],
    [2565, 'Shield Block', 'personal'],
    // Racials
    [20594, 'Stoneform', 'personal'],
    [59544, 'Gift of the Naaru', 'personal'],
    [265221, 'Fireblood', 'personal'],
    [58984, 'Shadowmeld', 'personal'],
]

export const DEFENSIVE_SPELLS = DEFENSIVE_LIST.reduce((map, [id, name, kind]) => {
    map[id] = { name, kind }
    return map
}, {})

export const DEFENSIVE_KIND_LABELS = {
    personal: 'Personal cooldown',
    external: 'Cast on them by someone else',
    immunity: 'Immunity',
    raid: 'Group wide cooldown',
}

export function isDefensiveSpell(spellId) {
    return Object.prototype.hasOwnProperty.call(DEFENSIVE_SPELLS, Number(spellId))
}

export function describeDefensive(spellId) {
    return DEFENSIVE_SPELLS[Number(spellId)] || null
}

export function describeSchool(schoolMask) {
    const mask = Number(schoolMask)
    if (!Number.isFinite(mask) || mask <= 0) return 'Unknown'
    if (SPELL_SCHOOLS[mask]) return SPELL_SCHOOLS[mask]

    const parts = Object.keys(SPELL_SCHOOLS)
        .map(Number)
        .filter((bit) => (mask & bit) === bit)
        .map((bit) => SPELL_SCHOOLS[bit])

    return parts.length ? parts.join(' / ') : `School ${mask}`
}

export function describeSpec(specId) {
    const spec = SPECIALIZATIONS[specId]
    if (spec) return spec
    if (!specId) return { className: '', spec: '', role: '' }
    return { className: '', spec: `Spec #${specId}`, role: '' }
}

export function describeDifficulty(difficultyId) {
    const id = Number(difficultyId)
    if (!id) return ''
    return DIFFICULTIES[id] || `Difficulty ${id}`
}

// Difficulties that mean "this log is an instance run", used to pick the zone a
// log should be labelled with when the player also walked through a city.
export const INSTANCE_DIFFICULTY_IDS = new Set([1, 2, 8, 14, 15, 16, 17, 23, 24, 33, 151, 167, 205, 208])

export function describeAffix(affixId) {
    return KNOWN_AFFIXES[Number(affixId)] || `Affix #${affixId}`
}

export function classColor(className) {
    return CLASS_COLORS[className] || ''
}
